import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { CLERK_SUPABASE_JWT_TEMPLATE, SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from '@/lib/env'

const CLERK_TOKEN_MAX_ATTEMPTS = 5
const CLERK_TOKEN_RETRY_MS = 150

type TokenGetter = ((opts?: { template: string }) => Promise<string | null>) | null

let getToken: TokenGetter = null
let supabaseClient: SupabaseClient | null = null

export class SupabaseDataError extends Error {
  status?: number
  code?: string

  constructor(message: string, options: { status?: number; code?: string } = {}) {
    super(message)
    this.name = 'SupabaseDataError'
    this.status = options.status
    this.code = options.code
  }
}

export function setSupabaseGetToken(fn: TokenGetter) {
  getToken = fn
}

function sleep(ms: number) {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

export async function getSupabaseAuthToken(): Promise<string> {
  const opts = CLERK_SUPABASE_JWT_TEMPLATE
    ? { template: CLERK_SUPABASE_JWT_TEMPLATE }
    : undefined
  let lastError = 'Clerk auth is not ready yet. The app would query Supabase as anonymous.'

  for (let attempt = 0; attempt < CLERK_TOKEN_MAX_ATTEMPTS; attempt += 1) {
    if (getToken) {
      const token = await getToken(opts)
      if (token) return token

      lastError = CLERK_SUPABASE_JWT_TEMPLATE
        ? `Clerk did not return a JWT for template "${CLERK_SUPABASE_JWT_TEMPLATE}".`
        : 'Clerk did not return a session JWT.'
    }

    if (attempt < CLERK_TOKEN_MAX_ATTEMPTS - 1) {
      await sleep(CLERK_TOKEN_RETRY_MS * (attempt + 1))
    }
  }

  throw new Error(`${lastError} Check the Clerk session and Supabase JWT template configuration for this environment.`)
}

export function hasSupabaseConfig(): boolean {
  return !!SUPABASE_URL && !!SUPABASE_PUBLISHABLE_KEY
}

export function getSupabaseClient(): SupabaseClient {
  if (!hasSupabaseConfig()) {
    throw new SupabaseDataError('Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.')
  }

  if (!supabaseClient) {
    supabaseClient = createClient(SUPABASE_URL!, SUPABASE_PUBLISHABLE_KEY!, {
      accessToken: () => getSupabaseAuthToken(),
    })
  }

  return supabaseClient
}

export async function disposeSupabaseClient() {
  if (supabaseClient) {
    await supabaseClient.removeAllChannels()
  }
  supabaseClient = null
}

export async function supabaseRpc<T>(
  name: string,
  args?: Record<string, unknown>
): Promise<T> {
  const { data, error } = await getSupabaseClient().rpc(name, args ?? {})
  if (error) {
    throw new SupabaseDataError(error.message, {
      code: error.code,
    })
  }
  return data as T
}
